import React, { useState, useCallback, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, IconButton, Tooltip, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  PlayArrow as PlayIcon, Save as SaveIcon, AddCircleOutline as AddIcon, FolderOpen as LoadIcon,
  TouchApp as TouchIcon, Description as TemplateIcon, AutoAwesome as AutoAwesomeIcon,
  AccountTree as AutoLayoutIcon, ContentPaste as ContentPasteIcon,
} from '@mui/icons-material';
import ComprehensivePalette from './ComprehensivePalette';
import FlowCanvas from './FlowCanvas';
import NodeConfigDialog from './NodeConfigDialog';
import FlowBuilderTabs from './FlowBuilderTabs';
import NodeContextMenu from './NodeContextMenu';
import PropertiesValidationPanel from './PropertiesValidationPanel';
import FlowTemplates from './FlowTemplates';
import FlowMinimap from './FlowMinimap';
import FlowChatBuilder from './FlowChatBuilder';
import { AutoLayout } from './AutoLayout';
import {
  useUndoRedo, UndoRedoControls, HistoryPanel, UndoRedoUtils, ACTION_TYPES,
} from './UndoRedoManager';
import {
  cloneLegacyNodeForPaste,
  readLegacyNodeClipboard,
  tryReadLegacyNodeSystemClipboard,
  wrapLegacyNodeEnvelope,
  writeLegacyNodeClipboard,
  writeLegacyNodeSystemClipboard,
} from './legacyFlowClipboardStorage';
import { getLangGraphWsUrl } from '../../utils/langgraphConfig.js';

// Default configurations for different node types
const getDefaultNodeConfig = (nodeType) => {
  const configs = {
    'ai-agent': {
      model: 'llama4',
      provider: 'llama-api',
      temperature: 0.3,
      maxTokens: 1000,
      systemPrompt: 'You are an expert Islamic scholar. Answer questions about Islam using ONLY the provided Quranic context.\n\n**Rules:**\n- Cite specific verses: "Quran, Surah [Name], Verse [Number]"\n- Only use information from the provided context\n- If information is not in context, say so clearly\n- Be scholarly and respectful\n\n**Format:**\n"According to the Quran, [answer].\n\nRelevant verses:\n- Quran, Surah [Name], Verse [Number]: "[text]"\n\n[Brief explanation if needed]"',
      tools: [],
      memory: true,
    },
    'code-executor': {
      language: 'javascript',
      code: '// Enter your code here\nreturn { result: "Hello World" };',
      timeout: 30000,
      environment: 'node',
    },
    'api-call': {
      method: 'GET',
      url: 'https://api.example.com/endpoint',
      headers: {},
      body: '',
      timeout: 10000,
      retries: 3,
    },
    'mcp-tool': {
      serverUrl: getLangGraphWsUrl('/api/v1/mcp/ws'),
      toolName: 'database_query',
      toolArguments: {
        query: 'SELECT * FROM table',
        parameters: [],
      },
      timeout: 30000,
      retries: 3,
      authentication: 'none',
      authToken: '',
    },
    webhook: {
      path: '/webhook',
      method: 'POST',
      authentication: 'none',
      responseFormat: 'json',
    },
    'web-crawler': {
      urls: [],
      urlVariable: '',
      depth: 0,
      maxPages: 10,
      extractMode: 'text',
      selectors: {},
      respectRobotsTxt: true,
      userAgent: '',
      requestDelayMs: 1000,
      maxConcurrent: 2,
      timeoutMs: 15000,
      retries: 2,
      outputVariable: 'crawled_content',
      includeHtml: false,
      allowedDomains: [],
    },
    connector: {
      connectorId: 'http',
      operationId: 'request',
      credentialRef: '',
      inputMapping: {},
      outputVariable: 'connector_result',
      stateVariables: {},
    },
    database: {
      operation: 'query',
      connection: 'default',
      query: 'SELECT * FROM table',
      parameters: {},
    },
    email: {
      to: '',
      subject: '',
      body: '',
      template: 'default',
      attachments: [],
    },
    sms: {
      to: '',
      message: '',
      provider: 'twilio',
    },
    conditional: {
      condition: 'input.value > 0',
      trueLabel: 'Yes',
      falseLabel: 'No',
    },
    loop: {
      type: 'forEach',
      array: 'input.items',
      maxIterations: 100,
      breakCondition: '',
    },
    transform: {
      inputSchema: {},
      outputSchema: {},
      transformation: 'return input;',
    },
    delay: {
      duration: 1000,
      unit: 'milliseconds',
    },
    scheduler: {
      type: 'cron',
      expression: '0 */5 * * * *',
      timezone: 'UTC',
    },
    policy: {
      mode: 'design',
      policy_model_id: '',
      collection: 'policy_docs',
      model_name: '',
      file_path: '',
      upload_status: '',
      output_to_context: true,
      output_variable: 'policy_model',
      run_query: false,
    },
    rag: {
      rag_mode: 'query',
      collection: 'documents',
      top_k: 5,
      similarity_threshold: 0.0,
      response_format: 'context_with_query',
      include_metadata: true,
      // Upload mode specific
      file_path: '',
      file_content: '',
      file_type: 'text',
      chunk_size: 1000,
      chunk_overlap: 200,
    },
    form: {
      fields: [
        {
          name: 'name', label: 'Name', type: 'text', required: true, default: '',
        },
        {
          name: 'email', label: 'Email', type: 'email', required: true, default: '',
        },
        {
          name: 'address', label: 'Address', type: 'address', required: false, default: '',
        },
      ],
      submitTo: 'database',
      table: '',
      apiEndpoint: '',
      submitLabel: 'Submit',
      successMessage: 'Form submitted successfully!',
      errorMessage: 'Submission failed.',
    },
    logger: {
      log_level: 'INFO',
      log_type: 'execution',
      message_template: 'Node {node_id} executed',
      include_state: false,
      include_timestamps: true,
      log_to_console: true,
      log_to_file: false,
      custom_fields: [],
    },
    debugger: {
      debug_mode: 'step',
      inspection_scope: 'state',
      breakpoint_condition: '',
      auto_continue: false,
      continue_timeout: 30,
      collect_stack_trace: true,
      inspect_variables: true,
      inspect_execution: true,
    },
    'execute-flow': {
      flowId: '',
      outputVariable: 'subflow_result',
      inputMapping: 'pass_through',
    },
  };

  return configs[nodeType] || {};
};

function FlowDesignTab({
  nodes = [], setNodes, edges = [], setEdges, globalState = {}, setGlobalState, darkMode, stateSchema, setStateSchema,
  selectedTab = 0, onTabChange = () => {}, onNewFlow = () => {}, onLoadFlow = () => {}, onSaveFlow = () => {}, onExecuteFlow = () => {},
  onNotify = () => {}, onNodeDelete = () => {},
  breakpoints = [], onToggleBreakpoint,
}) {
  const [selectedNode, setSelectedNode] = useState(null);
  const [connectionStart, setConnectionStart] = useState(null);
  const [contextMenu, setContextMenu] = useState({
    open: false,
    anchorEl: null,
    node: null,
  });

  // Debug: Force set to success for testing
  // React.useEffect(() => {
  //   setTimeout(() => setSelectedEdgeType('edge-success'), 2000);
  // }, []);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [nodeToConfig, setNodeToConfig] = useState(null);
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [nodeToDelete, setNodeToDelete] = useState(null);
  const [deleteEdgeDialogOpen, setDeleteEdgeDialogOpen] = useState(false);
  const [edgeToDelete, setEdgeToDelete] = useState(null);
  const [highlightedNodes, setHighlightedNodes] = useState([]);
  const [historyPanelOpen, setHistoryPanelOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [chatBuilderOpen, setChatBuilderOpen] = useState(false);
  const [minimapVisible, setMinimapVisible] = useState(true);
  const [viewport, setViewport] = useState({
    x: 0, y: 0, width: 800, height: 600, zoom: 1,
  });
  // Initialize undo/redo manager
  const undoManager = useUndoRedo({
    nodes: nodes || [],
    edges: edges || [],
    globalState: globalState || {},
  });

  const safeSetNodes = useCallback((updater) => {
    if (typeof setNodes === 'function') {
      setNodes(updater);
    }
  }, [setNodes]);

  const safeSetEdges = useCallback((updater) => {
    if (typeof setEdges === 'function') {
      setEdges(updater);
    }
  }, [setEdges]);

  const safeSetGlobalState = useCallback((updater) => {
    if (typeof setGlobalState === 'function') {
      setGlobalState(updater);
    }
  }, [setGlobalState]);

  // Undo/Redo handlers
  const handleUndo = useCallback(() => {
    const entry = undoManager.undo();
    if (entry && entry.data.snapshot) {
      const { nodes: snapshotNodes, edges: snapshotEdges, globalState: snapshotGlobalState } = entry.data.snapshot;
      safeSetNodes(snapshotNodes);
      safeSetEdges(snapshotEdges);
      safeSetGlobalState(snapshotGlobalState);
    }
  }, [undoManager, safeSetNodes, safeSetEdges, safeSetGlobalState]);

  const handleRedo = useCallback(() => {
    const entry = undoManager.redo();
    if (entry && entry.data.snapshot) {
      const { nodes: snapshotNodes, edges: snapshotEdges, globalState: snapshotGlobalState } = entry.data.snapshot;
      safeSetNodes(snapshotNodes);
      safeSetEdges(snapshotEdges);
      safeSetGlobalState(snapshotGlobalState);
    }
  }, [undoManager, safeSetNodes, safeSetEdges, safeSetGlobalState]);

  const handleClearHistory = useCallback(() => {
    undoManager.clearHistory();
  }, [undoManager]);

  const handleJumpToHistoryEntry = useCallback((index) => {
    // This would require more complex state management
    // For now, just close the history panel
    setHistoryPanelOpen(false);
  }, []);

  // Template handler
  const handleApplyTemplate = useCallback((template) => {
    // Create a snapshot for undo/redo
    const currentSnapshot = {
      nodes: nodes || [],
      edges: edges || [],
      globalState: globalState || {},
    };

    // Apply auto-layout to template nodes
    const layoutNodes = AutoLayout.smartLayout(template.nodes, template.edges, {
      direction: 'horizontal',
      spacing: { x: 300, y: 150 },
      startPosition: { x: 100, y: 100 },
      centerNodes: true,
    });

    // Apply the template with auto-layout
    safeSetNodes(layoutNodes);
    safeSetEdges(template.edges);
    safeSetGlobalState(template.globalState);

    // Track the action for undo/redo
    undoManager.addAction(ACTION_TYPES.BATCH_OPERATION, {
      operations: [{ type: 'APPLY_TEMPLATE', data: template }],
      snapshot: {
        nodes: layoutNodes,
        edges: template.edges,
        globalState: template.globalState,
      },
    });

    console.log('✅ Template applied with auto-layout:', template.name);
  }, [nodes, edges, globalState, safeSetNodes, safeSetEdges, safeSetGlobalState, undoManager]);

  // Chat flow generation handler
  const handleGenerateFlowFromChat = useCallback((generatedFlow) => {
    // Create a snapshot for undo/redo
    const currentSnapshot = {
      nodes: nodes || [],
      edges: edges || [],
      globalState: globalState || {},
    };

    // Apply auto-layout to generated flow nodes
    const layoutNodes = AutoLayout.smartLayout(generatedFlow.nodes, generatedFlow.edges, {
      direction: 'horizontal',
      spacing: { x: 300, y: 150 },
      startPosition: { x: 100, y: 100 },
      centerNodes: true,
    });

    // Apply the generated flow with auto-layout
    safeSetNodes(layoutNodes);
    safeSetEdges(generatedFlow.edges);
    safeSetGlobalState(generatedFlow.globalState || {});

    // Track the action for undo/redo
    undoManager.addAction(ACTION_TYPES.BATCH_OPERATION, {
      operations: [{ type: 'GENERATE_FROM_CHAT', data: generatedFlow }],
      snapshot: {
        nodes: layoutNodes,
        edges: generatedFlow.edges,
        globalState: generatedFlow.globalState || {},
      },
    });

    console.log('✅ Flow generated from chat with auto-layout:', generatedFlow);
  }, [nodes, edges, globalState, safeSetNodes, safeSetEdges, safeSetGlobalState, undoManager]);

  // Auto-layout handler for existing flows
  const handleAutoLayout = useCallback(() => {
    if (!nodes || nodes.length === 0) {
      console.log('⚠️ No nodes to layout');
      return;
    }

    console.log('🎨 Starting auto-layout for', nodes.length, 'nodes');

    // Create a snapshot for undo/redo
    const currentSnapshot = {
      nodes: nodes || [],
      edges: edges || [],
      globalState: globalState || {},
    };

    // Apply top-down hierarchical layout - flows vertically, branches horizontally
    const canvasWidth = 2000;
    const canvasHeight = 2000;
    const centerX = canvasWidth / 2; // Center horizontally for branches
    const startY = 150; // Start from top for top-down flow

    const layoutNodes = AutoLayout.applyLayout(nodes, edges, {
      direction: 'hierarchical',
      orientation: 'top-down',
      spacing: { x: 280, y: 200 },
      startPosition: { x: centerX, y: startY },
      centerNodes: true,
    });

    console.log('📐 Layout complete. Sample positions:', layoutNodes.slice(0, 3).map((n) => ({
      id: n.id,
      x: n.position.x,
      y: n.position.y,
    })));

    // Apply the layout
    safeSetNodes(layoutNodes);

    // Track the action for undo/redo
    undoManager.addAction(ACTION_TYPES.BATCH_OPERATION, {
      operations: [{ type: 'AUTO_LAYOUT', data: { nodes: layoutNodes } }],
      snapshot: currentSnapshot,
    });

    console.log('✅ Auto-layout applied with hierarchical distribution');
    console.log('📊 Canvas should now be wider. Use Fit Screen (⊡ icon) or scroll horizontally!');

    onNotify(`${layoutNodes.length} nodes organized. Use Fit Screen (⊡) to view the full layout.`, 'success');
  }, [nodes, edges, globalState, safeSetNodes, undoManager]);

  const handleNodeConnect = useCallback((sourceNode, targetNode) => {
    if (!sourceNode || !targetNode) {
      console.log('Missing source or target node');
      return false;
    }

    const existingEdge = edges.find((e) => e.source === sourceNode.id && e.target === targetNode.id);
    if (existingEdge) {
      console.log('Edge already exists');
      return false;
    }
    if (sourceNode.id === targetNode.id) {
      console.log('Cannot connect node to itself');
      return false;
    }

    const newEdge = {
      id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      source: sourceNode.id,
      target: targetNode.id,
      type: 'edge-basic', // Always create basic edges
      label: '',
      color: '#1976d2',
      strokeWidth: 3,
      animated: false,
      style: 'curved',
      pattern: 'solid',
      condition: '',
      data: {
        sourcePort: 'output',
        targetPort: 'input',
        sourceNodeType: sourceNode.type,
        targetNodeType: targetNode.type,
        createdAt: new Date().toISOString(),
      },
    };

    console.log('Creating edge:', newEdge.type, 'from', sourceNode.id, 'to', targetNode.id);

    const currentEdges = Array.isArray(edges) ? edges : [];
    const updatedEdges = [...currentEdges, newEdge];

    safeSetEdges(() => updatedEdges);

    console.log('Connection successful');
    return true;
  }, [edges, safeSetEdges]);

  // Enhanced node click handler with left-click connection and right-click context menu
  const handleNodeClick = useCallback((node, event, isDoubleClick = false, clickMode = 'select') => {
    console.log('Node click:', node.id, 'isDoubleClick:', isDoubleClick, 'clickMode:', clickMode, 'button:', event.button);

    // Handle double-click for configuration
    if (isDoubleClick || clickMode === 'config') {
      setSelectedNode(node);
      setNodeToConfig(node);
      setConfigDialogOpen(true);
      setConnectionStart(null);
      return;
    }

    // Handle right-click for context menu
    if (event.button === 2 || event.type === 'contextmenu') {
      event.preventDefault();
      event.stopPropagation();
      setContextMenu({
        open: true,
        anchorEl: event.currentTarget,
        node,
      });
      return;
    }

    // Handle left-click with modifier key for connections
    if (event.button === 0 || event.type === 'click') {
      const isModifierPressed = event.ctrlKey || event.metaKey || event.altKey;

      if (isModifierPressed) {
        // Modifier key pressed - handle connection
        if (connectionStart) {
          // Complete the connection
          if (connectionStart.id !== node.id) {
            const success = handleNodeConnect(connectionStart, node);
            if (success) {
              console.log('✅ Connection completed:', connectionStart.id, '→', node.id);
            }
          }
          setConnectionStart(null);
        } else {
          // Start connection
          console.log('🔗 Connection started from:', node.id, '(modifier key pressed)');
          setConnectionStart(node);
          setSelectedNode(node);
        }
      } else {
        // No modifier key - just select the node
        console.log('Node selected:', node.id);
        setSelectedNode(node);
        setConnectionStart(null); // Clear any existing connection start
      }
    }
  }, [connectionStart, handleNodeConnect]);

  // Context menu handlers
  const handleContextMenuClose = () => {
    setContextMenu({
      open: false,
      anchorEl: null,
      node: null,
    });
  };

  const handleContextMenuEdit = (node) => {
    setNodeToConfig(node);
    setConfigDialogOpen(true);
  };

  const handleContextMenuDelete = (node) => {
    setNodeToDelete({ id: node.id, label: node?.data?.label || node.id });
    setDeleteDialogOpen(true);
  };

  const handleContextMenuCopy = useCallback((node) => {
    if (!node) return;
    const env = wrapLegacyNodeEnvelope(node);
    if (!env) return;
    writeLegacyNodeClipboard(env);
    void writeLegacyNodeSystemClipboard(env).catch(() => {});
  }, []);

  const handlePasteLegacyNode = useCallback(async () => {
    let tpl = readLegacyNodeClipboard();
    if (!tpl) {
      try {
        tpl = await tryReadLegacyNodeSystemClipboard();
      } catch {
        /* clipboard permission */
      }
    }
    if (!tpl) {
      onNotify('No copied node — right-click a node and choose Copy Node (or copy from another tab with permission).', 'info');
      return;
    }
    const newNode = cloneLegacyNodeForPaste(tpl, nodes || [], getDefaultNodeConfig);
    safeSetNodes((prev) => {
      const updated = [...(prev || []), newNode];
      undoManager.addAction(ACTION_TYPES.ADD_NODE, {
        node: { ...newNode },
        snapshot: {
          nodes: updated,
          edges: edges || [],
          globalState: globalState || {},
        },
      });
      return updated;
    });
    setSelectedNode(newNode);
    setHighlightedNodes([newNode]);
    onNotify(`Pasted node "${newNode.id}"`, 'success');
  }, [nodes, edges, globalState, safeSetNodes, undoManager, onNotify]);

  const handleContextMenuConnect = (node) => {
    setConnectionStart(node);
    setSelectedNode(node);
  };

  // Handle confirmed node deletion
  const handleConfirmDelete = useCallback(() => {
    if (!nodeToDelete) return;

    const nodeId = nodeToDelete.id;
    const nodeToDeleteData = nodes.find((n) => n.id === nodeId);
    const edgesToDelete = edges.filter((e) => e.source === nodeId || e.target === nodeId);

    console.log('🗑️ Deleting node:', nodeId);
    console.log('🗑️ Current nodes before deletion:', nodes.length);
    console.log('🗑️ Current edges before deletion:', edges.length);

    safeSetNodes((prev) => {
      const filtered = (prev || []).filter((n) => n.id !== nodeId);
      console.log('🗑️ Nodes after deletion:', filtered.length);
      console.log('🗑️ Node IDs after deletion:', filtered.map((n) => n.id));
      return filtered;
    });

    safeSetEdges((prev) => {
      const filtered = (prev || []).filter((e) => e.source !== nodeId && e.target !== nodeId);
      console.log('🗑️ Edges after deletion:', filtered.length);

      // Track the action for undo/redo
      undoManager.addAction(ACTION_TYPES.DELETE_NODE, {
        node: nodeToDeleteData,
        deletedEdges: edgesToDelete,
        snapshot: {
          nodes: filtered,
          edges: filtered,
          globalState: globalState || {},
        },
      });

      return filtered;
    });

    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }

    console.log('🗑️ Node deletion completed for:', nodeId);
    setDeleteDialogOpen(false);
    setNodeToDelete(null);
  }, [nodeToDelete, nodes, edges, selectedNode, safeSetNodes, safeSetEdges, setSelectedNode, undoManager, globalState]);

  // Handle confirmed edge deletion
  const handleConfirmEdgeDelete = useCallback(() => {
    if (!edgeToDelete) return;

    console.log('🗑️ Deleting edge:', edgeToDelete.id);
    safeSetEdges((prev) => (prev || []).filter((e) => e.id !== edgeToDelete.id));

    console.log('🗑️ Edge deletion completed for:', edgeToDelete.id);
    setDeleteEdgeDialogOpen(false);
    setEdgeToDelete(null);
  }, [edgeToDelete, safeSetEdges]);

  // Initialize with start node if empty
  React.useEffect(() => {
    if (nodes.length === 0) {
      const startNode = {
        id: 'start',
        type: 'start',
        label: 'Start',
        position: { x: 100, y: 100 },
        data: {
          label: 'Start',
          type: 'start',
          config: {},
        },
      };
      safeSetNodes([startNode]);
    }
  }, [nodes.length, safeSetNodes]);

  const handleAddNode = useCallback((nodeType, position = null) => {
    // Use provided position or calculate a logical position based on existing nodes
    let newX = 200;
    let newY = 200;

    if (position) {
      // Use the dropped position
      newX = position.x;
      newY = position.y;
    } else if (nodes.length > 0) {
      // Find the rightmost node and place new node to its right
      const rightmostNode = nodes.reduce((rightmost, node) => (node.position.x > rightmost.position.x ? node : rightmost));
      newX = rightmostNode.position.x + 250; // 250px to the right
      newY = rightmostNode.position.y; // Same Y level

      // If we're too far right, wrap to a new row
      if (newX > 800) {
        newX = 200;
        // Find the bottommost node and place below it
        const bottommostNode = nodes.reduce((bottommost, node) => (node.position.y > bottommost.position.y ? node : bottommost));
        newY = bottommostNode.position.y + 200; // 200px below
      }
    }

    const newNode = {
      id: `${nodeType.type}_${Date.now()}`,
      type: nodeType.type,
      label: nodeType.label,
      position: {
        x: newX,
        y: newY,
      },
      data: {
        label: nodeType.label,
        type: nodeType.type,
        config: getDefaultNodeConfig(nodeType.type),
      },
    };

    safeSetNodes((prev) => {
      const updatedNodes = [...(prev || []), newNode];

      // Track the action for undo/redo
      undoManager.addAction(ACTION_TYPES.ADD_NODE, {
        node: { ...newNode },
        snapshot: {
          nodes: updatedNodes,
          edges: edges || [],
          globalState: globalState || {},
        },
      });

      return updatedNodes;
    });
  }, [safeSetNodes, nodes, edges, globalState, undoManager]);

  // Add updateNode function for quick-add in CustomNode
  const updateNode = useCallback((updatedNode) => {
    console.log('🔧 FlowDesignTab: Updating node', updatedNode);

    safeSetNodes((prev) => {
      // Find the node being updated
      const nodeIndex = prev.findIndex((n) => n.id === updatedNode.id);

      if (nodeIndex === -1) {
        console.error(`Can't find node with ID ${updatedNode.id}`);
        return prev;
      }

      const oldNode = prev[nodeIndex];

      // CRITICAL FIX: Just directly use the updated node instead of trying to merge
      // This ensures the stateVars array is correctly carried over
      const newNodes = [...prev];
      newNodes[nodeIndex] = updatedNode;

      // Log the change for debugging
      console.log('🔧 Node update complete:', {
        before: oldNode,
        after: updatedNode,
      });

      return newNodes;
    });
  }, [safeSetNodes]);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      width: { xs: '100vw', md: 'calc(100vw - var(--drawer-width, 240px))' }, // Use CSS custom property for drawer width
      minWidth: 0,
      minHeight: 0,
      position: 'relative', // For absolutely positioned minimap
    }}
    >
      {/* Header with Undo/Redo Controls */}
      <Box sx={{
        p: 1,
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontSize: '1rem' }}>
            Canvas
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            Ctrl/Cmd + click to connect • Drag nodes to move • Double-click to edit • Right-click → Copy Node
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Auto Layout - Organize nodes top-down for readability">
            <span>
              <IconButton
                size="small"
                onClick={() => {
                  console.log('🔘 Auto Layout button clicked');
                  handleAutoLayout();
                }}
                color="info"
                disabled={!nodes || nodes.length === 0}
                sx={{
                  bgcolor: nodes && nodes.length > 0 ? 'info.lighter' : 'transparent',
                  '&:hover': {
                    bgcolor: 'info.light',
                  },
                }}
              >
                <AutoLayoutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="AI Flow Builder">
            <IconButton
              size="small"
              onClick={() => setChatBuilderOpen(true)}
              color="secondary"
            >
              <AutoAwesomeIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Flow Templates">
            <IconButton
              size="small"
              onClick={() => setTemplatesDialogOpen(true)}
              color="primary"
            >
              <TemplateIcon />
            </IconButton>
          </Tooltip>
          <UndoRedoControls
            undoManager={undoManager}
            onUndo={handleUndo}
            onRedo={handleRedo}
            onClearHistory={handleClearHistory}
            showHistory
            onToggleHistory={() => setHistoryPanelOpen(true)}
          />
          <Tooltip title="Paste a node copied via right-click → Copy Node (same browser; optional system clipboard in another tab).">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentPasteIcon />}
              onClick={() => { void handlePasteLegacyNode(); }}
              sx={{ textTransform: 'none', minWidth: 0, px: 1.25 }}
            >
              Paste node
            </Button>
          </Tooltip>
        </Stack>
      </Box>

      {/* Main content row: Palette | Canvas | Properties | Validation */}
      <Box sx={{
        display: 'flex', flex: 1, minHeight: 0, minWidth: 0, gap: 2, overflow: 'hidden',
      }}
      >
        <ComprehensivePalette
          onAddNode={handleAddNode}
          onAddEdge={() => {}}
        />

        <Box sx={{ flex: 1, minWidth: 0, position: 'relative', display: 'flex' }}>
          {/* Welcoming empty-state overlay when only start node exists */}
          {nodes?.length === 1 && nodes[0]?.type === 'start' && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 10,
                textAlign: 'center',
                pointerEvents: 'none',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  maxWidth: 360,
                  bgcolor: 'background.paper',
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  pointerEvents: 'auto',
                }}
              >
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Build your flow
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Drag nodes from the palette onto the canvas, or start with a template
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<TemplateIcon />}
                    onClick={() => setTemplatesDialogOpen(true)}
                  >
                    Use template
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => setChatBuilderOpen(true)}
                  >
                    AI builder
                  </Button>
                </Stack>
              </Paper>
            </Box>
          )}
          <FlowCanvas
            nodes={nodes}
            edges={edges}
            breakpoints={breakpoints}
          onNodeSelect={handleNodeClick}
          selectedNode={selectedNode}
          onNodeDelete={(nodeId) => {
            console.log('🗑️ Node delete requested:', nodeId);
            const node = nodes.find((n) => n.id === nodeId);
            setNodeToDelete({ id: nodeId, label: node?.data?.label || nodeId });
            setDeleteDialogOpen(true);
          }}
          onCanvasClick={() => {
            setSelectedNode(null);
            setConnectionStart(null);
          }}
          onNodeConnect={handleNodeConnect}
          connectionStart={connectionStart}
          setConnectionStart={setConnectionStart}
          onNodeDrag={(nodeId, newPosition) => {
            safeSetNodes((prev) => (prev || []).map((node) => (node.id === nodeId
              ? { ...node, position: newPosition }
              : node)));
          }}
          onNodeConfigUpdate={() => {}}
          updateNode={updateNode}
          onEdgeDelete={(edge) => {
            console.log('🗑️ Edge delete requested:', edge.id);
            setEdgeToDelete(edge);
            setDeleteEdgeDialogOpen(true);
          }}
          onEdgeUpdate={(edgeId, updatedConfig) => {
            console.log('🔄 Updating edge configuration:', edgeId, updatedConfig);
            safeSetEdges((prev) => (prev || []).map((edge) => {
              if (edge.id === edgeId) {
                return {
                  ...edge,
                  ...updatedConfig,
                  data: {
                    ...edge.data,
                    ...updatedConfig,
                  },
                };
              }
              return edge;
            }));
          }}
          onAddNode={handleAddNode}
        />
        </Box>
        {/* Combined Properties & Validation Panel */}
        <PropertiesValidationPanel
          selectedNode={selectedNode}
          onNodeConfigUpdate={(node) => {
            setNodeToConfig(node);
            setConfigDialogOpen(true);
          }}
          onNodeDelete={onNodeDelete}
          nodes={nodes}
          edges={edges}
          globalState={globalState}
          onNodeClick={(node) => {
            setSelectedNode(node);
            setHighlightedNodes([node]);
          }}
          onHighlightNodes={setHighlightedNodes}
          collapsed={rightPanelCollapsed}
          onToggleCollapse={() => setRightPanelCollapsed(!rightPanelCollapsed)}
        />

        {/* Node Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setNodeToDelete(null);
          }}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Confirm Node Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete the node "
              {nodeToDelete?.label}
              "? This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDeleteDialogOpen(false);
              setNodeToDelete(null);
            }}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edge Delete Confirmation Dialog */}
        <Dialog
          open={deleteEdgeDialogOpen}
          onClose={() => {
            setDeleteEdgeDialogOpen(false);
            setEdgeToDelete(null);
          }}
          aria-labelledby="delete-edge-dialog-title"
          aria-describedby="delete-edge-dialog-description"
        >
          <DialogTitle id="delete-edge-dialog-title">
            Confirm Edge Deletion
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-edge-dialog-description">
              Are you sure you want to delete this connection?
              <br />
              <strong>This action cannot be undone.</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setDeleteEdgeDialogOpen(false);
                setEdgeToDelete(null);
              }}
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmEdgeDelete}
              color="error"
              variant="contained"
              autoFocus
            >
              Delete Edge
            </Button>
          </DialogActions>
        </Dialog>

        <NodeConfigDialog
          open={configDialogOpen}
          onClose={() => {
            setConfigDialogOpen(false);
            setNodeToConfig(null);
          }}
          node={nodeToConfig}
          nodes={nodes}
          onSave={(nodeId, newConfig) => {
            safeSetNodes((prev) => (prev || []).map((node) => (node.id === nodeId
              ? {
                ...node,
                data: {
                  ...node.data,
                  config: newConfig,
                },
              }
              : node)));
          }}
        />

        {/* Node Context Menu */}
        <NodeContextMenu
          open={contextMenu.open}
          anchorEl={contextMenu.anchorEl}
          onClose={handleContextMenuClose}
          node={contextMenu.node}
          onEdit={handleContextMenuEdit}
          onDelete={handleContextMenuDelete}
          onCopy={handleContextMenuCopy}
          onConnect={handleContextMenuConnect}
          onToggleBreakpoint={onToggleBreakpoint}
          breakpoints={breakpoints}
        />

        {/* History Panel */}
        <HistoryPanel
          undoManager={undoManager}
          open={historyPanelOpen}
          onClose={() => setHistoryPanelOpen(false)}
          onJumpToEntry={handleJumpToHistoryEntry}
        />

        {/* Flow Templates Dialog */}
        <FlowTemplates
          open={templatesDialogOpen}
          onClose={() => setTemplatesDialogOpen(false)}
          onApplyTemplate={handleApplyTemplate}
        />

        {/* AI Flow Builder Chat Dialog */}
        <FlowChatBuilder
          isOpen={chatBuilderOpen}
          onClose={() => setChatBuilderOpen(false)}
          onGenerateFlow={handleGenerateFlowFromChat}
          existingNodes={nodes}
          existingEdges={edges}
        />

        {/* Flow Minimap */}
        <FlowMinimap
          nodes={nodes}
          edges={edges}
          viewport={viewport}
          onViewportChange={setViewport}
          isVisible={minimapVisible}
          onToggleVisibility={() => setMinimapVisible(!minimapVisible)}
          position="top-right"
        />

      </Box>
    </Box>
  );
}

export default FlowDesignTab;
